import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Upload, FileText, Download, ShieldCheck } from 'lucide-react';
import { fmtDate } from '@/lib/format';
import { toast } from 'sonner';

const MAX = 10 * 1024 * 1024; // 10 MB

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result.split(',')[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export function PropertyDocuments({ propertyId, canManage }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('lease');
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/documents', { params: { property_id: propertyId } }).then(r => setItems(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (propertyId) load(); }, [propertyId]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > MAX) return toast.error('File too large (max 10 MB)');
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post('/documents', { name: file.name, category, content_base64: b64, mime_type: file.type, property_id: propertyId });
      toast.success('Document uploaded'); e.target.value = ''; load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
      e.target.value = '';
    } finally { setBusy(false); }
  };

  const download = async (d) => {
    const { data } = await api.get(`/documents/${d.id}/download`);
    const a = document.createElement('a');
    a.href = `data:${data.mime_type};base64,${data.content_base64}`;
    a.download = data.name; a.click();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
        <ShieldCheck className="h-3.5 w-3.5 text-[hsl(152_60%_34%)]" />
        Documents are screened by content moderation. Max 10 MB. Lease agreements, NOC, KYC, inspection reports, receipts, etc.
      </div>
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/60"><tr>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Name</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Category</th>
            <th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Uploaded</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Size</th>
            <th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-muted-foreground" /> {d.name}</td>
                <td className="px-3 py-2 capitalize">{d.category}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(d.created_at)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-xs">{Math.round((d.size || 0) / 1024)} KB</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => download(d)}><Download className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No documents yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {canManage && (
        <Card className="p-3 border-dashed flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
          <div className="flex-1">
            <Label className="text-xs">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-10" data-testid="document-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lease">Lease agreement</SelectItem>
                <SelectItem value="noc">NOC</SelectItem>
                <SelectItem value="kyc">KYC</SelectItem>
                <SelectItem value="inspection">Inspection report</SelectItem>
                <SelectItem value="receipt">Receipt</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="inline-flex items-center gap-2 rounded-lg border px-3 h-10 text-sm cursor-pointer hover:bg-muted/50">
            <Upload className="h-3.5 w-3.5" /> {busy ? 'Uploading…' : 'Upload file'}
            <input type="file" className="hidden" accept="application/pdf,image/*" onChange={upload} disabled={busy} data-testid="document-upload-input" />
          </label>
        </Card>
      )}
    </div>
  );
}
